import { createFileRoute } from '@tanstack/react-router'
import {
  type CompanyFileFilters,
  type CompanyFilePaginatedRequest,
  type CompanyFileSortingState,
  type Pagination,
  companyFileSortingColumns,
  FileContext,
} from 'types'
import z from 'zod'

import { queryCompanyFileList } from '@api/company/file/company-file.queries'

import { paginationSchema, sortingSchema } from '@/lib/table'

import { CompanyFiles } from '../../-components/company-files/company-files'

const searchSchema = z.object({
  pagination: paginationSchema,
  sorting: sortingSchema(companyFileSortingColumns),
  filters: z.object({ search: z.string().optional() }).optional(),
})

export const Route = createFileRoute(
  '/app/data/companies/$uuid/(mainPage)/debtor/files',
)({
  component: RouteComponent,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({
    params: { uuid },
    context: { queryClient },
    deps: { pagination, filters, sorting },
  }) => {
    queryClient.prefetchQuery(
      queryCompanyFileList({
        companyUuid: uuid,
        request: {
          ...pagination,
          pageIndex: pagination.pageIndex + 1,
          filters,
          sorting,
        },
      }),
    )
  },
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { uuid } = Route.useParams()
  const { pagination, filters, sorting } = Route.useSearch()

  const setPagination = (pagination: Pagination) =>
    navigate({ search: (previous) => ({ ...previous, pagination }) })

  const setSorting = (sorting: CompanyFileSortingState) =>
    navigate({ search: (previous) => ({ ...previous, sorting }) })

  const setFilters = (filters?: CompanyFileFilters) =>
    navigate({ search: (previous) => ({ ...previous, filters }) })

  const request: CompanyFilePaginatedRequest = {
    ...pagination,
    filters,
    sorting,
  }

  return (
    <CompanyFiles
      companyUuid={uuid}
      request={request}
      fileContext={FileContext.CompanyDebtor}
      setPagination={setPagination}
      setSorting={setSorting}
      setFilters={setFilters}
    />
  )
}
